/**
 * Pure, framework-free core for the shared top-layer popup portal.
 *
 * Lives as plain JS (not .ts) so it can be imported and behaviorally tested
 * by node:test with a tiny in-memory DOM shim — no jsdom/vitest needed —
 * AND imported by the React hook in top-layer-portal.ts. Keep this file
 * dependency-free (no React, no widget imports).
 *
 * INVARIANT — the portal root is a persistent, app-lifetime singleton. It is
 * an invisible (`pointer-events:none`) fixed container shared by EVERY widget
 * popup (scheduling-contact, leave-details, pre-session). It is intentionally
 * NEVER removed.
 *
 * WHY (regression guard): an earlier version deleted this root on every
 * popup's unmount (when its child count reached zero). With multiple popups
 * sharing one root, one popup unmounting deleted the shared node while another
 * popup still held a reference to it. React's createPortal then rendered into a
 * detached node and the popup was invisible — the recurring "no popup form was
 * shown" bug. Keeping the root persistent and self-healing makes that class of
 * bug impossible.
 */

export const TOP_LAYER_PORTAL_ID = 'iland-widget-top-layer-root'
export const TOP_LAYER_Z_INDEX = '2147483647'

export function applyPortalStyles(el) {
  Object.assign(el.style, {
    position: 'fixed',
    inset: '0',
    zIndex: TOP_LAYER_Z_INDEX,
    isolation: 'isolate',
    pointerEvents: 'none',
    transform: 'translateZ(0)',
    WebkitTransform: 'translateZ(0)',
  })
}

/**
 * Returns the single shared top-layer portal root, creating it if missing or
 * RE-ATTACHING it if something detached it. `doc` is injectable so the logic
 * can be tested with an in-memory DOM shim.
 *
 * @param {{ body: { appendChild: Function } | null, getElementById: Function, createElement: Function }} [doc]
 * @returns {HTMLDivElement | null}
 */
export function ensureTopLayerPortalIn(doc) {
  if (!doc || !doc.body) {
    return null
  }

  const existing = doc.getElementById(TOP_LAYER_PORTAL_ID)
  if (existing && existing.nodeType === 1) {
    // Self-heal: if something detached our root from the document, put it
    // back instead of leaving popups pointed at a dead node.
    if (existing.isConnected === false) {
      doc.body.appendChild(existing)
    }
    return existing
  }

  const portalRoot = doc.createElement('div')
  portalRoot.id = TOP_LAYER_PORTAL_ID
  applyPortalStyles(portalRoot)
  doc.body.appendChild(portalRoot)
  return portalRoot
}
